import { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useQueryClient } from '@tanstack/react-query';
import { updateGame, type GameItem } from '../../api/games';
import { AdminsSection } from './AdminsSection';

const STATUS_BASE_URL = `${window.location.protocol}//${window.location.host}/status`;

type FreqType = 'never' | 'daily' | 'weekly' | 'custom';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS: Record<string, string> = {
  monday: 'Montag', tuesday: 'Dienstag', wednesday: 'Mittwoch',
  thursday: 'Donnerstag', friday: 'Freitag', saturday: 'Samstag', sunday: 'Sonntag',
};

function parseSchedule(s: string | null): { freq: FreqType; day: string; time: string; custom: string } {
  if (!s) return { freq: 'never', day: 'monday', time: '04:00', custom: '' };
  if (s.startsWith('custom ')) return { freq: 'custom', day: 'monday', time: '04:00', custom: s.slice(7) };
  const dailyM = /^daily (\d{2}:\d{2})$/.exec(s);
  if (dailyM) return { freq: 'daily', day: 'monday', time: dailyM[1], custom: '' };
  const weeklyM = /^weekly (\w+) (\d{2}:\d{2})$/.exec(s);
  if (weeklyM) return { freq: 'weekly', day: weeklyM[1], time: weeklyM[2], custom: '' };
  return { freq: 'never', day: 'monday', time: '04:00', custom: '' };
}

function buildSchedule(freq: FreqType, day: string, time: string, custom: string): string | null {
  if (freq === 'never') return null;
  if (freq === 'daily') return `daily ${time}`;
  if (freq === 'weekly') return `weekly ${day} ${time}`;
  return custom.trim() ? `custom ${custom.trim()}` : null;
}

interface Props {
  game: GameItem;
  onClose: () => void;
}

export function EditGameDialog({ game, onClose }: Props) {
  const qc = useQueryClient();
  const [name, setName] = useState(game.name);
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [publicStatus, setPublicStatus] = useState(game.public_status ?? false);

  const initial = parseSchedule(game.update?.scheduled ?? null);
  const [schedFreq, setSchedFreq] = useState<FreqType>(initial.freq);
  const [schedDay, setSchedDay] = useState(initial.day);
  const [schedTime, setSchedTime] = useState(initial.time);
  const [schedCustom, setSchedCustom] = useState(initial.custom);
  const [backupBefore, setBackupBefore] = useState(game.update?.backup_before ?? true);
  const [restartAfter, setRestartAfter] = useState(game.update?.restart_after ?? true);

  const [betaBranch, setBetaBranch] = useState(game.steam_beta_branch ?? '');
  const [mods, setMods] = useState<number[]>(game.workshop_mods ?? []);
  const [newModId, setNewModId] = useState('');

  const arInitial = parseSchedule(game.auto_restart?.schedule ?? null);
  const [arEnabled, setArEnabled] = useState(game.auto_restart?.enabled ?? false);
  const [arFreq, setArFreq] = useState<FreqType>(arInitial.freq);
  const [arDay, setArDay] = useState(arInitial.day);
  const [arTime, setArTime] = useState(arInitial.time);
  const [arCustom, setArCustom] = useState(arInitial.custom);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    setBusy(true);
    setError(null);
    try {
      const payload: Parameters<typeof updateGame>[1] = {};
      if (name !== game.name) payload.name = name;
      if (thumbnailUrl) payload.thumbnail_url = thumbnailUrl;
      if (publicStatus !== (game.public_status ?? false)) payload.public_status = publicStatus;

      const newSchedule = buildSchedule(schedFreq, schedDay, schedTime, schedCustom);
      if (newSchedule !== (game.update?.scheduled ?? null)) payload.update_scheduled = newSchedule;
      if (backupBefore !== (game.update?.backup_before ?? true)) payload.update_backup_before = backupBefore;
      if (restartAfter !== (game.update?.restart_after ?? true)) payload.update_restart_after = restartAfter;

      const arNewSchedule = buildSchedule(arFreq, arDay, arTime, arCustom);
      if (arEnabled !== (game.auto_restart?.enabled ?? false)) payload.auto_restart_enabled = arEnabled;
      if (arNewSchedule !== (game.auto_restart?.schedule ?? null)) payload.auto_restart_schedule = arNewSchedule;

      const branchVal = betaBranch.trim() || null;
      if (branchVal !== (game.steam_beta_branch ?? null)) payload.steam_beta_branch = branchVal;
      if (JSON.stringify(mods) !== JSON.stringify(game.workshop_mods ?? [])) payload.workshop_mods = mods;

      await updateGame(game.id, payload);
      await qc.invalidateQueries({ queryKey: ['games'] });
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Game — {game.id}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Game ID" value={game.id} disabled size="small" fullWidth />
          <TextField
            label="Provider"
            value={game.provider}
            disabled
            size="small"
            fullWidth
            sx={{ textTransform: 'capitalize' }}
          />
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            size="small"
            fullWidth
            inputProps={{ maxLength: 100 }}
          />
          <TextField
            label="New thumbnail URL (leave blank to keep current)"
            value={thumbnailUrl}
            onChange={(e) => setThumbnailUrl(e.target.value)}
            size="small"
            fullWidth
            placeholder="https://..."
          />

          {/* Public status toggle */}
          <Stack spacing={0.5}>
            <FormControlLabel
              control={
                <Switch
                  checked={publicStatus}
                  onChange={(e) => setPublicStatus(e.target.checked)}
                  size="small"
                />
              }
              label={<Typography variant="body2">Öffentliche Statusseite aktivieren</Typography>}
            />
            {publicStatus && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  label={`${STATUS_BASE_URL}/${game.id}`}
                  size="small"
                  variant="outlined"
                  sx={{ fontFamily: 'monospace', fontSize: '0.7rem', maxWidth: 260 }}
                />
                <Tooltip title="URL kopieren">
                  <Button
                    size="small"
                    sx={{ minWidth: 0, p: 0.5 }}
                    onClick={() => void navigator.clipboard.writeText(`${STATUS_BASE_URL}/${game.id}`)}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </Button>
                </Tooltip>
              </Stack>
            )}
          </Stack>

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>

        {/* Scheduled updates accordion */}
        <Accordion disableGutters sx={{ mt: 2, '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2" fontWeight={500}>
              Geplante Updates
              {schedFreq !== 'never' && (
                <Chip label="aktiv" size="small" color="info" sx={{ ml: 1 }} />
              )}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80 }}>
                  Häufigkeit
                </Typography>
                <Select
                  value={schedFreq}
                  onChange={(e) => setSchedFreq(e.target.value as FreqType)}
                  size="small"
                  sx={{ minWidth: 160 }}
                >
                  <MenuItem value="never">Nie</MenuItem>
                  <MenuItem value="daily">Täglich</MenuItem>
                  <MenuItem value="weekly">Wöchentlich</MenuItem>
                  <MenuItem value="custom">Eigener Zeitplan</MenuItem>
                </Select>
              </Stack>

              {(schedFreq === 'daily' || schedFreq === 'weekly') && (
                <Stack direction="row" spacing={1} alignItems="center">
                  {schedFreq === 'weekly' && (
                    <>
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80 }}>
                        Wochentag
                      </Typography>
                      <Select
                        value={schedDay}
                        onChange={(e) => setSchedDay(e.target.value)}
                        size="small"
                        sx={{ minWidth: 130 }}
                      >
                        {DAYS.map((d) => (
                          <MenuItem key={d} value={d}>{DAY_LABELS[d]}</MenuItem>
                        ))}
                      </Select>
                    </>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: schedFreq === 'weekly' ? 0 : 80, ml: schedFreq === 'weekly' ? 1 : 0 }}>
                    {schedFreq === 'weekly' ? 'um' : 'Uhrzeit'}
                  </Typography>
                  <TextField
                    type="time"
                    value={schedTime}
                    onChange={(e) => setSchedTime(e.target.value)}
                    size="small"
                    inputProps={{ step: 300 }}
                    sx={{ width: 120 }}
                  />
                </Stack>
              )}

              {schedFreq === 'custom' && (
                <TextField
                  label="OnCalendar-Ausdruck (systemd)"
                  value={schedCustom}
                  onChange={(e) => setSchedCustom(e.target.value)}
                  size="small"
                  fullWidth
                  placeholder="z.B. Mon,Wed *-*-* 03:00:00"
                  helperText="Wird direkt als systemd OnCalendar verwendet."
                />
              )}

              {schedFreq !== 'never' && (
                <Stack spacing={0.5}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={backupBefore}
                        onChange={(e) => setBackupBefore(e.target.checked)}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">Backup vor Update erstellen</Typography>}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={restartAfter}
                        onChange={(e) => setRestartAfter(e.target.checked)}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">Server nach Update neu starten</Typography>}
                  />
                </Stack>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Auto-Restart accordion */}
        <Accordion disableGutters sx={{ mt: 1, '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2" fontWeight={500}>
              Automatischer Neustart
              {arFreq !== 'never' && (
                <Chip label="aktiv" size="small" color="info" sx={{ ml: 1 }} />
              )}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={arEnabled}
                    onChange={(e) => setArEnabled(e.target.checked)}
                    size="small"
                  />
                }
                label={<Typography variant="body2">Automatischen Neustart aktivieren</Typography>}
              />

              {arEnabled && (
                <>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80 }}>
                      Häufigkeit
                    </Typography>
                    <Select
                      value={arFreq}
                      onChange={(e) => setArFreq(e.target.value as FreqType)}
                      size="small"
                      sx={{ minWidth: 160 }}
                    >
                      <MenuItem value="never">Nie</MenuItem>
                      <MenuItem value="daily">Täglich</MenuItem>
                      <MenuItem value="weekly">Wöchentlich</MenuItem>
                      <MenuItem value="custom">Eigener Zeitplan</MenuItem>
                    </Select>
                  </Stack>

                  {(arFreq === 'daily' || arFreq === 'weekly') && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      {arFreq === 'weekly' && (
                        <>
                          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80 }}>
                            Wochentag
                          </Typography>
                          <Select
                            value={arDay}
                            onChange={(e) => setArDay(e.target.value)}
                            size="small"
                            sx={{ minWidth: 130 }}
                          >
                            {DAYS.map((d) => (
                              <MenuItem key={d} value={d}>{DAY_LABELS[d]}</MenuItem>
                            ))}
                          </Select>
                        </>
                      )}
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: arFreq === 'weekly' ? 0 : 80, ml: arFreq === 'weekly' ? 1 : 0 }}>
                        {arFreq === 'weekly' ? 'um' : 'Uhrzeit'}
                      </Typography>
                      <TextField
                        type="time"
                        value={arTime}
                        onChange={(e) => setArTime(e.target.value)}
                        size="small"
                        inputProps={{ step: 300 }}
                        sx={{ width: 120 }}
                      />
                    </Stack>
                  )}

                  {arFreq === 'custom' && (
                    <TextField
                      label="OnCalendar-Ausdruck (systemd)"
                      value={arCustom}
                      onChange={(e) => setArCustom(e.target.value)}
                      size="small"
                      fullWidth
                      placeholder="z.B. Mon,Wed *-*-* 03:00:00"
                      helperText="Wird direkt als systemd OnCalendar verwendet."
                    />
                  )}
                </>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Workshop Mods & Beta Branch accordion */}
        <Accordion disableGutters sx={{ mt: 1, '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2" fontWeight={500}>
              SteamCMD &amp; Workshop Mods
              {mods.length > 0 && (
                <Chip label={`${mods.length} mod${mods.length !== 1 ? 's' : ''}`} size="small" color="secondary" sx={{ ml: 1 }} />
              )}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <TextField
                label="Beta branch (leave blank for stable)"
                value={betaBranch}
                onChange={(e) => setBetaBranch(e.target.value)}
                size="small"
                fullWidth
                placeholder="experimental"
                helperText="Applied on next install or update via SteamCMD"
              />
              <Stack spacing={1}>
                <Typography variant="caption" color="text.secondary">Workshop Mod IDs</Typography>
                {mods.map((id) => (
                  <Stack key={id} direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', flexGrow: 1 }}>{id}</Typography>
                    <IconButton size="small" onClick={() => setMods((prev) => prev.filter((m) => m !== id))}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
                <TextField
                  size="small"
                  label="Add Workshop Mod ID"
                  value={newModId}
                  onChange={(e) => setNewModId(e.target.value.replace(/\D/g, ''))}
                  placeholder="e.g. 1234567890"
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button
                          size="small"
                          disabled={!newModId || mods.includes(Number(newModId))}
                          onClick={() => {
                            const n = Number(newModId);
                            if (n > 0 && !mods.includes(n)) setMods((prev) => [...prev, n]);
                            setNewModId('');
                          }}
                        >
                          Add
                        </Button>
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>
            </Stack>
          </AccordionDetails>
        </Accordion>

        <Divider sx={{ my: 2 }} />
        <AdminsSection gameId={game.id} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button
          variant="contained"
          disabled={busy || !name.trim()}
          onClick={() => void handleSave()}
          startIcon={busy ? <CircularProgress size={16} /> : undefined}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
